import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Header } from '../components/layout/Header';
import { melaApi } from '../api/modules';
import { StatusPill } from '../components/ui/StatusPill';
import { MelaCampDrawer } from '../drawers/MelaCampDrawer';
import { MelaScheduleModal } from '../drawers/MelaScheduleModal';

export function MelaPage() {
  const [status, setStatus] = useState<string>('any');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string | null>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const summary = useQuery({ queryKey: ['mela-summary'], queryFn: melaApi.summary });
  const list = useQuery({
    queryKey: ['mela-list', { status, page }],
    queryFn: () => melaApi.list({ page, page_size: 25, status }),
    placeholderData: (prev) => prev,
  });

  return (
    <>
      <Header breadcrumbs={['Modules', 'Pashu Arogyasheel Melas']} />
      <div className="px-8 pt-7 pb-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-navy-800/50 mb-1">Module M05</div>
          <h1 className="serif-display text-3xl text-navy-800 font-medium">Pashu Arogyasheel Melas</h1>
          <p className="text-sm text-navy-800/60 mt-1">
            Camp calendar · On-site capture · Disease surveillance · Deendayal Upadhyay initiative
          </p>
        </div>
      </div>

      <div className="px-8 py-5 space-y-5 overflow-y-auto">
        {/* Summary cards */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-white border border-cream-200 p-4 rounded-sm">
            <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Total camps</div>
            <div className="font-numeric serif-display text-2xl text-navy-800 font-medium mt-1">
              {summary.data?.total ?? '…'}
            </div>
            <div className="text-[11px] text-navy-800/60 mt-1.5">FY 2025-26</div>
          </div>
          <div className="bg-white border border-cream-200 p-4 rounded-sm">
            <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Scheduled</div>
            <div className="font-numeric serif-display text-2xl text-navy-800 font-medium mt-1">
              {summary.data?.scheduled ?? '…'}
            </div>
            <div className="text-[11px] text-saffron-600 mt-1.5">upcoming</div>
          </div>
          <div className="bg-white border border-cream-200 p-4 rounded-sm">
            <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Completed</div>
            <div className="font-numeric serif-display text-2xl text-navy-800 font-medium mt-1">
              {summary.data?.completed ?? '…'}
            </div>
            <div className="text-[11px] text-green-700 mt-1.5">camps run</div>
          </div>
          <div className="bg-white border border-cream-200 p-4 rounded-sm">
            <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Beneficiaries served</div>
            <div className="font-numeric serif-display text-2xl text-navy-800 font-medium mt-1">
              {summary.data ? summary.data.beneficiaries_served.toLocaleString('en-IN') : '…'}
            </div>
            <div className="text-[11px] text-navy-800/60 mt-1.5">cumulative</div>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white border border-cream-200 rounded-sm p-4 flex items-center gap-3 text-xs">
          <span className="text-navy-800/60">Filter:</span>
          {(['any', 'scheduled', 'in_progress', 'completed', 'cancelled'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => { setStatus(s); setPage(1); }}
              className={`px-3 py-1.5 rounded-sm transition ${status === s ? 'bg-navy-800 text-white' : 'bg-cream-100 text-navy-800/80 hover:bg-cream-200'}`}
            >
              {s === 'any' ? 'All' : s.replace('_', ' ')}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setScheduleOpen(true)}
            className="ml-auto px-3 py-1.5 bg-saffron-500 hover:bg-saffron-600 text-white rounded-sm transition flex items-center gap-2"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5v14" /></svg>
            Schedule mela
          </button>
        </div>

        {/* Table */}
        <div className="bg-white border border-cream-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cream-50">
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Camp ID</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Date</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Nyaya Panchayat</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Mandal / District</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Lead</th>
                <th className="text-right px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Target</th>
                <th className="text-right px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Actual</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading || !list.data ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-t border-cream-100"><td colSpan={8} className="px-3 py-3"><div className="h-3 bg-cream-100 rounded animate-pulse" /></td></tr>
                ))
              ) : (
                list.data.data.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => setSelected(c.id)}
                    className="border-t border-cream-100 hover:bg-cream-50 cursor-pointer"
                  >
                    <td className="px-3 py-2.5 font-numeric text-navy-800/70 text-xs">{c.id}</td>
                    <td className="px-3 py-2.5 font-numeric text-navy-800">
                      {new Date(c.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                    </td>
                    <td className="px-3 py-2.5 text-navy-800/80">{c.np ?? '—'}</td>
                    <td className="px-3 py-2.5 text-navy-800/70 text-xs">{c.mandal} · {c.district}</td>
                    <td className="px-3 py-2.5 text-navy-800/80 text-xs">{c.lead}</td>
                    <td className="px-3 py-2.5 text-right font-numeric text-navy-800/70">{c.target.toLocaleString('en-IN')}</td>
                    <td className={`px-3 py-2.5 text-right font-numeric font-medium ${
                      c.actual === null ? 'text-navy-800/40' :
                      c.actual >= c.target ? 'text-green-700' : 'text-red-600'
                    }`}>
                      {c.actual !== null ? c.actual.toLocaleString('en-IN') : '—'}
                    </td>
                    <td className="px-3 py-2.5"><StatusPill status={c.status} /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {list.data && list.data.pagination.total_pages > 1 && (
            <div className="px-5 py-3 border-t border-cream-100 flex items-center justify-end gap-2 text-xs">
              <button type="button" disabled={page === 1} onClick={() => setPage(p => p - 1)}
                className="px-3 py-1.5 bg-cream-100 text-navy-800/80 rounded-sm hover:bg-cream-200 disabled:opacity-50">← Previous</button>
              <button type="button" disabled={page >= list.data.pagination.total_pages} onClick={() => setPage(p => p + 1)}
                className="px-3 py-1.5 bg-navy-800 text-white rounded-sm hover:bg-navy-700 disabled:opacity-50">Next →</button>
            </div>
          )}
        </div>
      </div>
      <MelaCampDrawer open={!!selected} code={selected} onClose={() => setSelected(null)} />
      <MelaScheduleModal open={scheduleOpen} onClose={() => setScheduleOpen(false)} />
    </>
  );
}
